import { useState } from 'react';
import type {
  SystemDetailsData,
  HeatSource,
  DHWArchitecture,
  CircuitType,
  ControlTopology,
  EmitterType,
  LowTempDesign,
  CylinderSpace,
  TemperatureControls,
} from '../types';
import { BOILER_OUTPUT_BANDS } from '@constants/boilerOutputBands';

const SOURCE_OPTIONS: { value: HeatSource; label: string }[] = [
  { value: 'gas', label: 'Gas' },
  { value: 'oil', label: 'Oil' },
  { value: 'lpg', label: 'LPG' },
  { value: 'electric', label: 'Electric' },
  { value: 'heat-pump', label: 'Heat pump' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'unknown', label: 'Unknown' },
];

const ARCH_OPTIONS: { value: DHWArchitecture; label: string }[] = [
  { value: 'combi-on-demand', label: 'Combi (on-demand)' },
  { value: 'stored-dhw', label: 'Stored DHW' },
  { value: 'unknown', label: 'Unknown' },
];

const CIRCUIT_OPTIONS: { value: CircuitType; label: string }[] = [
  { value: 'sealed', label: 'Sealed' },
  { value: 'open-vented', label: 'Open-vented' },
  { value: 'unknown', label: 'Unknown' },
];

const TOPOLOGY_OPTIONS: { value: ControlTopology; label: string }[] = [
  { value: 'y-plan', label: 'Y-plan' },
  { value: 's-plan', label: 'S-plan' },
  { value: 's-plan-plus', label: 'S-plan+' },
  { value: 'combi', label: 'Combi (no valve plan)' },
  { value: 'gravity-dhw', label: 'Gravity DHW + pumped CH' },
  { value: 'unknown', label: 'Unknown' },
];

const EMITTER_OPTIONS: { value: EmitterType; label: string }[] = [
  { value: 'radiators', label: 'Radiators' },
  { value: 'ufh', label: 'Underfloor heating' },
  { value: 'mixed', label: 'Mixed (rads + UFH)' },
  { value: 'unknown', label: 'Unknown' },
];

const LOW_TEMP_OPTIONS: { value: LowTempDesign; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Unknown' },
];

const CYLINDER_SPACE_OPTIONS: { value: CylinderSpace; label: string; hint: string }[] = [
  { value: 'yes-existing', label: 'Yes — cylinder already there', hint: 'Existing cylinder location can be reused' },
  { value: 'yes-available', label: 'Yes — space available', hint: 'Room for a new cylinder (airing cupboard, utility, etc.)' },
  { value: 'no', label: 'No', hint: 'No practical space for a cylinder' },
  { value: 'unknown', label: 'Not assessed', hint: '' },
];

const TEMP_CONTROLS_OPTIONS: { value: TemperatureControls; label: string }[] = [
  { value: 'basic', label: 'Basic' },
  { value: 'programmable-stat', label: 'Programmable stat' },
  { value: 'load-compensation', label: 'Load compensation' },
  { value: 'smart', label: 'Smart' },
  { value: 'smart-load-compensation', label: 'Smart load compensation' },
  { value: 'unknown', label: 'Unknown' },
];

// SEDBUK band → efficacy (%) when new
const SEDBUK_BAND_EFFICACY: Record<string, number> = {
  A: 90,
  B: 86,
  C: 82,
  D: 78,
  E: 74,
  F: 70,
  G: 65,
  'Pre SEDBUK': 60,
};

const SEDBUK_BAND_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'Pre SEDBUK'] as const;

interface Props {
  data: SystemDetailsData;
  onSave: (data: SystemDetailsData) => void;
  onClose: () => void;
}

export function SystemDetailsScreen({ data, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<SystemDetailsData>({ ...data });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const set = <K extends keyof SystemDetailsData>(key: K, value: SystemDetailsData[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSedbukBandSelect = (band: string) => {
    const efficacy = SEDBUK_BAND_EFFICACY[band];
    setDraft(prev => ({
      ...prev,
      sedbukBand: band,
      sedbukRating: efficacy !== undefined ? String(efficacy) : prev.sedbukRating,
    }));
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      onClose();
    }
  };

  const handleSave = () => {
    setHasUnsavedChanges(false);
    onSave(draft);
  };

  return (
    <>
      <div className="hub-screen-overlay">
      <div className="hub-screen" onClick={e => e.stopPropagation()}>
        <div className="hub-screen__header">
          <h2 className="hub-screen__title">System Details</h2>
          <button className="hub-screen__close" onClick={handleClose}>&times;</button>
        </div>

        <div className="hub-screen__body">
          <div className="hub-field">
            <label className="hub-field__label">Heat source (primary)</label>
            <div className="hub-field__chip-group">
              {SOURCE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.heatSource === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('heatSource', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">DHW architecture</label>
            <div className="hub-field__chip-group">
              {ARCH_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.dhwArchitecture === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('dhwArchitecture', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Space for a cylinder</label>
            <span className="hub-field__hint">
              Is there space for a hot water cylinder (existing or available)?
            </span>
            <div className="hub-field__chip-group">
              {CYLINDER_SPACE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.cylinderSpace === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('cylinderSpace', opt.value)}
                  title={opt.hint}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">CH circuit type ("Is sealed best?" — depends on context)</label>
            <span className="hub-field__hint">
              Sealed is not universally better; suitability depends on existing layout, upgrade scope, and target system.
            </span>
            <div className="hub-field__chip-group">
              {CIRCUIT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.circuitType === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('circuitType', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Current configuration</label>
            <div className="hub-field__chip-group">
              {TOPOLOGY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.controlTopology === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('controlTopology', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Emitters type</label>
            <div className="hub-field__chip-group">
              {EMITTER_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.emitters === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('emitters', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Low-temp suitability</label>
            <span className="hub-field__hint">
              Radiators default "no unless proven"; UFH defaults "yes but medium confidence"
            </span>
            <div className="hub-field__chip-group">
              {LOW_TEMP_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.lowTempDesign === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('lowTempDesign', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Evidence (optional)</label>
            <input
              type="text"
              placeholder="e.g. heat loss calc, site survey, etc."
              value={draft.lowTempEvidence}
              onChange={e => set('lowTempEvidence', e.target.value)}
            />
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Boiler GC Number (optional)</label>
            <span className="hub-field__hint">
              For SEDBUK efficiency lookup (e.g., 47-116-30)
            </span>
            <input
              type="text"
              placeholder="e.g., 47-116-30"
              value={draft.gcNumber}
              onChange={e => set('gcNumber', e.target.value)}
            />
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Boiler make (optional)</label>
            <input
              type="text"
              placeholder="e.g., Worcester Bosch"
              value={draft.boilerMake}
              onChange={e => set('boilerMake', e.target.value)}
            />
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Boiler model (optional)</label>
            <input
              type="text"
              placeholder="e.g., Greenstar 30CDi"
              value={draft.boilerModel}
              onChange={e => set('boilerModel', e.target.value)}
            />
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Boiler output (kW)</label>
            <span className="hub-field__hint">
              Rated heat output capacity of the boiler
            </span>
            <select
              value={draft.boilerOutput}
              onChange={e => set('boilerOutput', e.target.value)}
            >
              <option value="">Select output...</option>
              {BOILER_OUTPUT_BANDS.map(band => (
                <option key={band} value={band.toLowerCase().replace(/kw/i, '')}>
                  {band}
                </option>
              ))}
            </select>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">SEDBUK band</label>
            <span className="hub-field__hint">
              Selecting a band sets the efficacy (when new): A=90%, B=86%, C=82%, D=78%, E=74%, F=70%, G=65%, Pre SEDBUK=60%
            </span>
            <div className="hub-field__chip-group">
              {SEDBUK_BAND_OPTIONS.map(band => (
                <button
                  key={band}
                  className={`hub-chip${draft.sedbukBand === band ? ' hub-chip--selected' : ''}`}
                  onClick={() => handleSedbukBandSelect(band)}
                >
                  {band}
                </button>
              ))}
            </div>
          </div>

          <div className="hub-field">
            <label className="hub-field__label">SEDBUK rating % (optional)</label>
            <input
              type="number"
              min="0"
              max="100"
              placeholder="e.g., 94"
              value={draft.sedbukRating}
              onChange={e => set('sedbukRating', e.target.value)}
            />
          </div>

          <div className="hub-field">
            <label className="hub-field__label">System age (optional, years)</label>
            <input
              type="number"
              min="0"
              placeholder="e.g., 15"
              value={draft.systemAge}
              onChange={e => set('systemAge', e.target.value)}
            />
          </div>

          <div className="hub-field">
            <label className="hub-field__label">Existing temperature controls (incl. hall thermostat if present)</label>
            <span className="hub-field__hint">
              Control strategy is contextual: a hall stat can work well in some layouts and poorly in others.
            </span>
            <div className="hub-field__chip-group">
              {TEMP_CONTROLS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`hub-chip${draft.temperatureControls === opt.value ? ' hub-chip--selected' : ''}`}
                  onClick={() => set('temperatureControls', opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="hub-screen__footer">
          <button className="hub-screen__button hub-screen__button--secondary" onClick={handleClose}>Cancel</button>
          <button className="hub-screen__button hub-screen__button--primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>

    {/* Discard changes dialog */}
    {showDiscardDialog && (
      <div className="hub-screen-overlay" style={{ zIndex: 1001 }}>
        <div className="hub-screen hub-screen--dialog" onClick={e => e.stopPropagation()}>
          <div className="hub-screen__header">
            <h2 className="hub-screen__title">Discard changes?</h2>
          </div>
          <div className="hub-screen__body">
            <p>You have unsaved changes. Are you sure you want to discard them?</p>
          </div>
          <div className="hub-screen__footer">
            <button 
              className="hub-screen__button hub-screen__button--secondary" 
              onClick={() => setShowDiscardDialog(false)}
            >
              Keep editing
            </button>
            <button 
              className="hub-screen__button hub-screen__button--danger" 
              onClick={() => {
                setShowDiscardDialog(false);
                setHasUnsavedChanges(false);
                onClose();
              }}
            >
              Discard changes
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
